import * as React from "react"
import { cn } from "@/lib/utils"

export interface ConfirmationInputModalProps {
  /**
   * The message to display in the modal
   */
  message: string
  /**
   * The word that must be typed to confirm (case-insensitive)
   */
  confirmationWord: string
  /**
   * Label for the input field
   * @default "Type '{confirmationWord}' to confirm"
   */
  inputLabel?: string
  /**
   * Placeholder text for the input field
   */
  inputPlaceholder?: string
  /**
   * Text for the cancel/no button
   * @default "CANCEL"
   */
  cancelText?: string
  /**
   * Text for the confirm/yes button
   * @default "CONFIRM"
   */
  confirmText?: string
  /**
   * Callback when cancel button is clicked
   */
  onCancel: () => void
  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void
  /**
   * Whether the confirm action is in progress
   * @default false
   */
  isLoading?: boolean
  /**
   * Additional CSS classes for the modal container
   */
  className?: string
}

/**
 * Renders markdown-formatted message text.
 * Supports **bold** syntax only for backward compatibility.
 *
 * @param message - Plain text or text with **bold** markdown
 * @returns Array of React nodes with proper formatting
 */
function renderMessageWithMarkdown(message: string): React.ReactNode {
  // Simple regex to match **text** for bold
  const boldPattern = /\*\*(.+?)\*\*/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Find all bold markdown patterns
  while ((match = boldPattern.exec(message)) !== null) {
    // Add text before the bold part
    if (match.index > lastIndex) {
      parts.push(message.substring(lastIndex, match.index))
    }
    // Add bold text
    parts.push(
      <strong key={match.index} className="font-bold">
        {match[1]}
      </strong>
    )
    lastIndex = match.index + match[0].length
  }

  // Add remaining text after last bold part
  if (lastIndex < message.length) {
    parts.push(message.substring(lastIndex))
  }

  // If no markdown found, return original message
  return parts.length > 0 ? parts : message
}

/**
 * Confirmation modal with typed verification
 *
 * A confirmation dialog that requires the user to type a specific word
 * before the action can be confirmed. Useful for destructive actions
 * like account deletion.
 *
 * Follows ANYW3AR design specifications with input validation.
 *
 * @example
 * ```tsx
 * <ConfirmationInputModal
 *   message="This action will permanently delete your account and all data."
 *   confirmationWord="DELETE"
 *   onCancel={() => console.log('cancelled')}
 *   onConfirm={() => console.log('confirmed')}
 * />
 * ```
 */
export const ConfirmationInputModal = React.forwardRef<HTMLDivElement, ConfirmationInputModalProps>(
  (
    {
      message,
      confirmationWord,
      inputLabel,
      inputPlaceholder,
      cancelText = "CANCEL",
      confirmText = "CONFIRM",
      onCancel,
      onConfirm,
      isLoading = false,
      className,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState("")

    const normalizedInput = inputValue.trim().toLowerCase()
    const normalizedWord = confirmationWord.trim().toLowerCase()
    const isMatch = normalizedInput === normalizedWord

    const defaultLabel = inputLabel ?? `Type "${confirmationWord}" to confirm`

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && isMatch && !isLoading) {
        onConfirm()
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-4 items-center justify-center",
          "bg-gray-100 rounded-[0.3125rem] p-4",
          "w-full",
          className
        )}
      >
        {/* Message with markdown support */}
        <p className="font-sf-pro text-[0.9375rem] text-black leading-normal w-full whitespace-pre-wrap">
          {renderMessageWithMarkdown(message)}
        </p>

        {/* Input Field */}
        <div className="w-full">
          <label className="block font-sf-pro text-[0.75rem] text-gray-700 mb-1">
            {defaultLabel}
          </label>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder ?? confirmationWord}
            disabled={isLoading}
            className={cn(
              "w-full px-3 py-2",
              "font-sf-pro text-[0.9375rem] text-black",
              "rounded-[0.375rem] border-2",
              "transition-colors duration-200",
              "focus:outline-none focus:ring-0",
              isMatch
                ? "border-brand-green bg-green-50"
                : "border-gray-300 bg-white",
              isLoading && "opacity-50 cursor-not-allowed"
            )}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-4 w-full">
          {/* Cancel Button */}
          <button
            onClick={onCancel}
            disabled={isLoading}
            className={cn(
              "flex-1 flex items-center justify-center",
              "bg-white rounded-[0.375rem]",
              "px-4 py-2 h-[2.3556rem]",
              "font-montserrat font-bold text-[0.9375rem] text-black text-center uppercase",
              "hover:bg-gray-50 active:bg-gray-100",
              "transition-colors",
              isLoading && "opacity-50 cursor-not-allowed"
            )}
          >
            {cancelText}
          </button>

          {/* Confirm Button */}
          <button
            onClick={onConfirm}
            disabled={!isMatch || isLoading}
            className={cn(
              "flex-1 flex items-center justify-center",
              "rounded-[0.375rem]",
              "px-4 py-2 h-[2.3556rem]",
              "font-montserrat font-bold text-[0.9375rem] text-center uppercase",
              "transition-colors",
              isMatch && !isLoading
                ? "bg-brand-red text-white hover:bg-red-700 active:bg-red-800"
                : "bg-gray-400 text-gray-200 cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {confirmText}
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    )
  }
)

ConfirmationInputModal.displayName = "ConfirmationInputModal"
